import { useEffect } from 'react';

import { hasPermission, type Permission } from '@/core/auth/permissions.js';
import { sessionStore, storedRefreshToken } from '@/core/auth/session.store.js';

import { refresh } from '../infrastructure/auth.api.js';

/** Lectura de la sesión actual para la UI. */
export function useSession() {
  const status = sessionStore((state) => state.status);
  const user = sessionStore((state) => state.user);

  return {
    status,
    user,
    isAuthenticated: status === 'autenticada',
    can: (permission: Permission) => hasPermission(user?.permissions, permission),
  };
}

/**
 * Rehidrata la sesión al abrir la app.
 *
 * El access token vive en memoria y se pierde al recargar, así que aquí se
 * canjea el refresh guardado. Mientras tanto el estado es "desconocida" para
 * que el router no expulse al usuario antes de saber si tiene sesión.
 */
export function useRestoreSession(): void {
  const status = sessionStore((state) => state.status);
  const setSession = sessionStore((state) => state.setSession);
  const markAnonymous = sessionStore((state) => state.markAnonymous);

  useEffect(() => {
    if (status !== 'desconocida') {
      return;
    }
    const token = storedRefreshToken();
    if (token === undefined) {
      markAnonymous();
      return;
    }

    let cancelado = false;
    refresh(token)
      .then((session) => {
        if (!cancelado) setSession(session);
      })
      .catch(() => {
        if (!cancelado) markAnonymous();
      });

    return () => {
      cancelado = true;
    };
  }, [status, setSession, markAnonymous]);
}
